import { useState, useRef } from 'react'
import { Upload, Search, Youtube, Image, Film, Music, Link as LinkIcon } from 'lucide-react'

/**
 * MediaDock - Drag & Drop Zone for Media Content
 *
 * Features:
 * - Drag & drop YouTube links, images, videos, audio
 * - Manual URL paste input
 * - YouTube search integration
 * - File upload support
 * - Auto-detect media type from URLs
 *
 * @param {Function} onMediaAdd - Callback when media is added (url, type, metadata)
 * @param {Boolean} disabled - Disable interactions (non-admin users)
 */
export default function MediaDock({ onMediaAdd, disabled = false }) {
  const [isDragging, setIsDragging] = useState(false)
  const [urlInput, setUrlInput] = useState('')
  const [isProcessing, setIsProcessing] = useState(false)
  const [showSearch, setShowSearch] = useState(false)
  const fileInputRef = useRef(null)

  // Drag and drop handlers
  const handleDragEnter = (e) => {
    e.preventDefault()
    e.stopPropagation()
    if (!disabled) {
      setIsDragging(true)
    }
  }

  const handleDragLeave = (e) => {
    e.preventDefault()
    e.stopPropagation()
    setIsDragging(false)
  }

  const handleDragOver = (e) => {
    e.preventDefault()
    e.stopPropagation()
  }

  const handleDrop = async (e) => {
    e.preventDefault()
    e.stopPropagation()
    setIsDragging(false)

    if (disabled) return

    const items = e.dataTransfer.items
    const files = e.dataTransfer.files

    // Check for text/URL drop
    for (let i = 0; i < items.length; i++) {
      if (items[i].type === 'text/plain' || items[i].type === 'text/uri-list') {
        items[i].getAsString((url) => {
          handleUrlSubmit(url.trim())
        })
        return
      }
    }

    // Check for file drop
    if (files.length > 0) {
      handleFileUpload(files[0])
    }
  }

  // Detect media type from URL
  const detectMediaType = (url) => {
    const urlLower = url.toLowerCase()

    // YouTube
    if (urlLower.includes('youtube.com/watch') ||
        urlLower.includes('youtu.be/') ||
        urlLower.includes('youtube.com/shorts/')) {
      return 'youtube'
    }

    // Image extensions
    if (urlLower.match(/\.(jpg|jpeg|png|gif|webp|svg)(\?|$)/)) {
      return 'image'
    }

    // Video extensions
    if (urlLower.match(/\.(mp4|webm|mov|avi|mkv)(\?|$)/)) {
      return 'video'
    }

    // Audio extensions
    if (urlLower.match(/\.(mp3|wav|ogg|m4a|flac)(\?|$)/)) {
      return 'audio'
    }

    // Default to link
    return 'link'
  }

  // Extract YouTube video ID
  const extractYouTubeId = (url) => {
    const patterns = [
      /(?:youtube\.com\/watch\?v=|youtu\.be\/|youtube\.com\/shorts\/)([^&\n?#]+)/,
      /youtube\.com\/embed\/([^&\n?#]+)/
    ]

    for (const pattern of patterns) {
      const match = url.match(pattern)
      if (match) return match[1]
    }

    return null
  }

  // Fetch YouTube metadata (title, thumbnail)
  const fetchYouTubeMetadata = async (videoId) => {
    // For now, return basic metadata
    // In production, integrate with YouTube Data API
    return {
      title: `YouTube Video: ${videoId}`,
      thumbnail: `https://img.youtube.com/vi/${videoId}/hqdefault.jpg`
    }
  }

  // Handle URL submission
  const handleUrlSubmit = async (url) => {
    if (!url || disabled) return

    setIsProcessing(true)

    try {
      const mediaType = detectMediaType(url)
      let metadata = {
        title: url,
        thumbnail: null,
        duration: null
      }

      // Fetch YouTube metadata if applicable
      if (mediaType === 'youtube') {
        const videoId = extractYouTubeId(url)
        if (videoId) {
          const ytMetadata = await fetchYouTubeMetadata(videoId)
          metadata.title = ytMetadata.title
          metadata.thumbnail = ytMetadata.thumbnail
        }
      }

      // Call the onMediaAdd callback
      onMediaAdd?.({
        type: mediaType,
        url: url,
        title: metadata.title,
        thumbnail: metadata.thumbnail,
        duration: metadata.duration
      })

      // Clear input
      setUrlInput('')
    } catch (error) {
      console.error('Error processing URL:', error)
    } finally {
      setIsProcessing(false)
    }
  }

  // Handle file upload
  const handleFileUpload = async (file) => {
    if (!file || disabled) return

    setIsProcessing(true)

    try {
      // Detect file type
      let mediaType = 'link'
      if (file.type.startsWith('image/')) {
        mediaType = 'image'
      } else if (file.type.startsWith('video/')) {
        mediaType = 'video'
      } else if (file.type.startsWith('audio/')) {
        mediaType = 'audio'
      }

      // In production, upload to Supabase Storage
      // For now, create a local object URL
      const fileUrl = URL.createObjectURL(file)

      onMediaAdd?.({
        type: mediaType,
        url: fileUrl,
        title: file.name,
        thumbnail: mediaType === 'image' ? fileUrl : null,
        duration: null,
        file: file // Pass file for actual upload
      })
    } catch (error) {
      console.error('Error uploading file:', error)
    } finally {
      setIsProcessing(false)
    }
  }

  // Handle paste input
  const handlePaste = (e) => {
    const pastedText = e.clipboardData.getData('text')
    if (pastedText) {
      setUrlInput(pastedText)
      // Auto-submit if it looks like a URL
      if (pastedText.startsWith('http://') || pastedText.startsWith('https://')) {
        setTimeout(() => handleUrlSubmit(pastedText), 100)
      }
    }
  }

  return (
    <div className="h-full flex flex-col">
      {/* Main Drop Zone */}
      <div
        className={`
          flex-1 flex flex-col items-center justify-center
          border-2 border-dashed rounded-xl
          transition-all duration-300
          ${isDragging
            ? 'border-gjyl-indigo-500 bg-gjyl-indigo-500/10 scale-[0.98]'
            : 'border-gjyl-slate-700 bg-gjyl-slate-900/30'
          }
          ${disabled ? 'opacity-50 cursor-not-allowed' : 'hover:border-gjyl-indigo-500/50 cursor-pointer'}
        `}
        onDragEnter={handleDragEnter}
        onDragLeave={handleDragLeave}
        onDragOver={handleDragOver}
        onDrop={handleDrop}
        onClick={() => !disabled && fileInputRef.current?.click()}
      >
        <div className="text-center space-y-6 p-12 max-w-2xl">
          {/* Animated Icon */}
          <div className={`text-8xl transition-transform duration-300 ${isDragging ? 'scale-110' : 'scale-100'}`}>
            {isProcessing ? (
              <div className="animate-spin text-gjyl-indigo-500">⚙️</div>
            ) : (
              <div className="animate-pulse">🎬</div>
            )}
          </div>

          {/* Title */}
          <h2 className="text-2xl font-bold text-white">
            {isDragging ? 'Drop to add media' : 'Drag media here to start watching'}
          </h2>

          {/* Description */}
          <p className="text-slate-400 max-w-md mx-auto">
            Drop YouTube links, images, videos, or audio files. Supports drag & drop, paste, or file upload.
          </p>

          {/* Supported Types */}
          <div className="flex flex-wrap gap-3 justify-center pt-4">
            <div className="flex items-center gap-2 px-3 py-1.5 glass rounded-full text-sm">
              <Youtube className="w-4 h-4 text-red-400" />
              <span className="text-slate-300">YouTube</span>
            </div>
            <div className="flex items-center gap-2 px-3 py-1.5 glass rounded-full text-sm">
              <Image className="w-4 h-4 text-green-400" />
              <span className="text-slate-300">Images</span>
            </div>
            <div className="flex items-center gap-2 px-3 py-1.5 glass rounded-full text-sm">
              <Film className="w-4 h-4 text-blue-400" />
              <span className="text-slate-300">Videos</span>
            </div>
            <div className="flex items-center gap-2 px-3 py-1.5 glass rounded-full text-sm">
              <Music className="w-4 h-4 text-purple-400" />
              <span className="text-slate-300">Audio</span>
            </div>
          </div>

          {/* URL Input */}
          <div className="pt-6" onClick={(e) => e.stopPropagation()}>
            <form
              onSubmit={(e) => {
                e.preventDefault()
                handleUrlSubmit(urlInput)
              }}
              className="flex gap-2 max-w-xl mx-auto"
            >
              <div className="flex-1 relative">
                <Search className="absolute left-3 top-1/2 -translate-y-1/2 w-5 h-5 text-slate-500" />
                <input
                  type="text"
                  value={urlInput}
                  onChange={(e) => setUrlInput(e.target.value)}
                  onPaste={handlePaste}
                  placeholder="🔍 Paste YouTube URL or search..."
                  className="input-gjyl w-full pl-10"
                  disabled={disabled || isProcessing}
                />
              </div>
              <button
                type="submit"
                className="btn-primary"
                disabled={!urlInput.trim() || disabled || isProcessing}
              >
                Add
              </button>
            </form>
          </div>

          {/* Upload Button */}
          <div className="pt-2" onClick={(e) => e.stopPropagation()}>
            <button
              onClick={() => fileInputRef.current?.click()}
              className="btn-secondary inline-flex items-center gap-2"
              disabled={disabled}
            >
              <Upload className="w-4 h-4" />
              Upload File
            </button>
            <input
              ref={fileInputRef}
              type="file"
              accept="image/*,video/*,audio/*"
              className="hidden"
              onChange={(e) => {
                if (e.target.files?.[0]) {
                  handleFileUpload(e.target.files[0])
                }
              }}
            />
          </div>

          {/* Disabled Message */}
          {disabled && (
            <div className="glass-strong rounded-lg p-4 max-w-md mx-auto">
              <p className="text-sm text-slate-400">
                🔒 Only admins and users with media control permission can add media to this channel.
              </p>
            </div>
          )}
        </div>
      </div>

      {/* Quick Tips */}
      {!disabled && (
        <div className="mt-4 glass rounded-lg p-4">
          <div className="flex items-start gap-3">
            <div className="text-2xl">💡</div>
            <div className="flex-1 space-y-1">
              <p className="text-sm font-medium text-white">Quick Tips:</p>
              <ul className="text-xs text-slate-400 space-y-1">
                <li>• Drag YouTube links directly from your browser</li>
                <li>• Paste URLs with Ctrl+V for instant detection</li>
                <li>• Upload files up to 100MB (video/audio/images)</li>
                <li>• Media will be added to the queue automatically</li>
              </ul>
            </div>
          </div>
        </div>
      )}
    </div>
  )
}
